// Device status view model — GET /api/v1/provisioning/fleet → venue list +
// flat device list for the 設備管理 page. Pure functions, no React, no fetch.
//
// Liveness comes from the API (`device.connectivity.state`, computed server-side
// from deviceops_devices.token_last_seen_at by lib/deviceLiveness.js:
// online ≤ 5 min, delayed ≤ 10 min, offline > 10 min, never_connected when the
// device has never authenticated). The portal never recomputes it from
// timestamps. Display power (TV on / standby) is not observable today, so the
// page shows a fixed note instead of guessing.

export const LIVENESS = Object.freeze({
  online: { label: "在線", tone: "success" },
  delayed: { label: "連線延遲", tone: "warning" },
  offline: { label: "離線", tone: "danger" },
  never_connected: { label: "從未連線", tone: "dim" },
  unavailable: { label: "無法判定", tone: "dim" },
});

export const DISPLAY_POWER_NOTE = "螢幕電源狀態：無法偵測";

// Exact API values only. Legacy/future/missing values are not ACTIVE.
export function lifecycleOf(device) {
  if (device?.lifecycle_state === "ACTIVE") return "active";
  if (device?.lifecycle_state === "REVOKED" || device?.lifecycle_state === "RETIRED") return "disabled";
  return "unknown";
}

export function livenessOf(device) {
  const state = device && device.connectivity && device.connectivity.state;
  return Object.prototype.hasOwnProperty.call(LIVENESS, state) ? state : "unavailable";
}

const orNull = (value) => (value === undefined || value === null || value === "" ? null : value);

function toRow(device, venueName) {
  const lifecycle = lifecycleOf(device);
  return {
    id: device.id,
    name: orNull(device.device_name) || device.id,
    venueId: orNull(device.venue_id),
    venueName: venueName || null,
    lifecycle,
    lifecycleState: device.lifecycle_state ?? null,
    state: lifecycle === "active" ? livenessOf(device) : lifecycle === "disabled" ? "disabled" : "unavailable",
    lastSeenAt: orNull(device.connectivity && device.connectivity.last_reported_at),
    lastSyncAt: orNull(device.last_sync_at),
    appVersion: orNull(device.app_version),
  };
}

// The fleet payload nests devices under venues[].zones[].devices and
// venues[].unzoned_devices, and lists devices whose venue is not one of the
// account's live venues under unbound_devices. Each device appears once.
export function flattenFleet(data) {
  const payload = data || {};
  const venues = [];
  const devices = [];
  const seen = new Set();
  const add = (device, venueName) => {
    if (!device || !device.id || seen.has(device.id)) return;
    seen.add(device.id);
    devices.push(toRow(device, venueName));
  };

  for (const venue of Array.isArray(payload.venues) ? payload.venues : []) {
    if (!venue || !venue.id) continue;
    const zones = Array.isArray(venue.zones) ? venue.zones : [];
    venues.push({
      id: venue.id,
      name: orNull(venue.name) || venue.id,
      zones: zones.filter((z) => z && z.id).map((z) => ({ id: z.id, name: orNull(z.name) })),
    });
    for (const zone of zones) {
      for (const device of Array.isArray(zone && zone.devices) ? zone.devices : []) add(device, venue.name || venue.id);
    }
    for (const device of Array.isArray(venue.unzoned_devices) ? venue.unzoned_devices : []) add(device, venue.name || venue.id);
  }
  for (const device of Array.isArray(payload.unbound_devices) ? payload.unbound_devices : []) add(device, null);

  return { venues, devices };
}

export function countByState(devices) {
  const counts = { total: 0, disabled: 0, unknown_lifecycle: 0 };
  for (const state of Object.keys(LIVENESS)) counts[state] = 0;
  for (const device of devices) {
    if (device.lifecycle === "disabled") counts.disabled += 1;
    else if (device.lifecycle !== "active") counts.unknown_lifecycle += 1;
    else {
      counts.total += 1;
      counts[Object.prototype.hasOwnProperty.call(LIVENESS, device.state) ? device.state : "unavailable"] += 1;
    }
  }
  return counts;
}

export function fmtTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("zh-TW", {
    timeZone: "Asia/Taipei",
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", hour12: false,
  });
}
